import { useEffect } from 'react';

export default function useDeafModeAutoMute() {
  useEffect(() => {
    // Never let MR translate media UI
    const stripFromMedia = () => {
      document
        .querySelectorAll('video, audio, [class*="video"], [data-video]')
        .forEach((el) => el.classList && el.classList.remove('mr-tooltip'));
    };
    stripFromMedia();
    setTimeout(stripFromMedia, 500);

    const isVendorNode = (n) =>
      n &&
      n.nodeType === 1 &&
      (n.id?.includes('mr-') ||
        n.className?.toString?.().includes('mr-') ||
        (n.src && n.src.includes('mindrocketsapis.com')));

    // Consider Deaf mode ON if we see their player/audio anywhere
    const isDeafOn = () =>
      !!(
        document.querySelector('.mr-tooltip-player, #mr-player') ||
        document.querySelector('[id*="mr-"] audio, [class*="mr-"] audio') ||
        document.querySelector(
          'audio[src*="mindrocketsapis.com"], video[src*="mindrocketsapis.com"]'
        ) ||
        document.querySelector('iframe[src*="mindrocketsapis.com"]')
      );

    const mediaEls = () => document.querySelectorAll('video, audio');
    const prev = new WeakMap();
    let lastState = null;

    const muteAll = () => {
      mediaEls().forEach((m) => {
        // skip vendor media if any leaks into <audio>/<video> (paranoia)
        if (isVendorNode(m)) return;
        if (!prev.has(m))
          prev.set(m, {
            wasPaused: m.paused,
            muted: m.muted,
            volume: m.volume,
          });
        m.muted = true;
        m.pause(); // change to m.play() if you want silent playback
      });

      // Best-effort: YouTube & Vimeo embeds
      document
        .querySelectorAll('iframe[src*="youtube.com/embed"]')
        .forEach((f) => {
          try {
            f.contentWindow?.postMessage(
              JSON.stringify({ event: 'command', func: 'mute', args: [] }),
              '*'
            );
          } catch {
            console.log('');
          }
        });
      document
        .querySelectorAll('iframe[src*="player.vimeo.com"]')
        .forEach((f) => {
          try {
            f.contentWindow?.postMessage(
              { method: 'setVolume', value: 0 },
              '*'
            );
          } catch {
            console.log('');
          }
        });
    };

    const restoreAll = () => {
      mediaEls().forEach((m) => {
        if (isVendorNode(m)) return;
        const p = prev.get(m);
        if (!p) return;
        m.muted = p.muted;
        m.volume = p.volume;
        if (!p.wasPaused)
          m.play().catch(() => {
            console.log('');
          });
        prev.delete(m);
      });

      // Optional unmute for embeds
      document
        .querySelectorAll('iframe[src*="youtube.com/embed"]')
        .forEach((f) => {
          try {
            f.contentWindow?.postMessage(
              JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
              '*'
            );
          } catch {
            console.log('');
          }
        });
      document
        .querySelectorAll('iframe[src*="player.vimeo.com"]')
        .forEach((f) => {
          try {
            f.contentWindow?.postMessage(
              { method: 'setVolume', value: 1 },
              '*'
            );
          } catch {
            console.log('');
          }
        });
    };

    const apply = () => {
      const on = isDeafOn();
      if (on === lastState) return;
      lastState = on;
      // tiny global flag if you want your components to react
      window.__DEAF_MODE_ON__ = on;
      if (on) {
        stripFromMedia();
        muteAll();
      } else {
        restoreAll();
      }
    };

    // 1) Observe DOM changes (fast, reliable)
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'childList') {
          // if vendor nodes were added/removed, re-evaluate
          const touchedVendor = [...m.addedNodes, ...m.removedNodes].some(
            isVendorNode
          );
          if (touchedVendor) {
            apply();
            return;
          }
        }
        if (m.type === 'attributes' && isVendorNode(m.target)) {
          apply();
          return;
        }
      }
    });
    mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'id', 'src'],
    });

    // 2) Poll as a safety net (handles late iframes, cross-origin)
    const interval = setInterval(apply, 700);

    // First run
    apply();

    return () => {
      mo.disconnect();
      clearInterval(interval);
    };
  }, []);
}
